/**
 * Goafreet Custom Cursor State Manager
 * Handles low-frequency semantic states (loading, success, error, explicit mode)
 * and loading ownership tracking without triggering high-frequency React re-renders.
 */

// Global state container
const state = {
  activeMode: "default",      // default | link | button | card | text | input | drag | open | view | play | download | copy | loading | success | error | native
  label: "",
  loadingOwners: new Set(),
  temporaryTimer: null,
};

const listeners = new Set();

function notify() {
  const snapshot = {
    activeMode: state.activeMode,
    label: state.label,
    isLoading: state.loadingOwners.size > 0,
  };
  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (e) {
      console.error("Cursor state listener error:", e);
    }
  });
}

export const cursorState = {
  getSnapshot() {
    return {
      activeMode: state.activeMode,
      label: state.label,
      isLoading: state.loadingOwners.size > 0,
    };
  },

  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  setMode(mode, label = "") {
    if (state.temporaryTimer) {
      clearTimeout(state.temporaryTimer);
      state.temporaryTimer = null;
    }
    state.activeMode = mode;
    state.label = label;
    notify();
  },

  reset() {
    if (state.temporaryTimer) {
      clearTimeout(state.temporaryTimer);
      state.temporaryTimer = null;
    }
    state.activeMode = state.loadingOwners.size > 0 ? "loading" : "default";
    state.label = "";
    notify();
  },

  // Ownership-based loading state (avoids race conditions between async tasks)
  startLoading(ownerKey = "default") {
    state.loadingOwners.add(ownerKey);
    state.activeMode = "loading";
    state.label = "";
    notify();
  },

  stopLoading(ownerKey = "default") {
    state.loadingOwners.delete(ownerKey);
    if (state.loadingOwners.size === 0) {
      state.activeMode = "default";
      state.label = "";
    }
    notify();
  },

  // Transient success state (checkmark + pulse, 600-900ms)
  triggerSuccess(durationMs = 800) {
    if (state.temporaryTimer) clearTimeout(state.temporaryTimer);
    state.activeMode = "success";
    state.label = "✓";
    notify();

    state.temporaryTimer = setTimeout(() => {
      state.activeMode = state.loadingOwners.size > 0 ? "loading" : "default";
      state.label = "";
      state.temporaryTimer = null;
      notify();
    }, durationMs);
  },

  // Transient error state (× + shake, 600-900ms)
  triggerError(durationMs = 800) {
    if (state.temporaryTimer) clearTimeout(state.temporaryTimer);
    state.activeMode = "error";
    state.label = "×";
    notify();

    state.temporaryTimer = setTimeout(() => {
      state.activeMode = state.loadingOwners.size > 0 ? "loading" : "default";
      state.label = "";
      state.temporaryTimer = null;
      notify();
    }, durationMs);
  },
};
